import { useEffect, useState } from 'react';
import {
  FaArrowLeft,
  FaMinus,
  FaPlus,
  FaSave,
  FaShoppingCart,
  FaTag,
  FaTimes,
  FaUser,
} from 'react-icons/fa';
import productService from '../../utils/productService';
import { computeTotals } from '../../utils/orderService';

function toPhoneLike(value) {
  return String(value || '').replace(/[^\d+\-() ]/g, '');
}

const OrderForm = ({ initial = null, onSubmit, onCancel }) => {
  const [products, setProducts] = useState([]);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    productService.getAll().then((response) => setProducts(response.data || [])).catch(() => setProducts([]));
  }, []);

  const [customerName, setCustomerName] = useState(initial?.customerName || '');
  const [phone, setPhone] = useState(initial?.phone || '');
  const [discount, setDiscount] = useState(initial?.discount ?? 0);
  const [items, setItems] = useState(
    initial?.items?.length
      ? initial.items.map((i) => ({
          productId: i.productId ?? '',
          name: i.name || '',
          quantity: i.quantity ?? 1,
          price: i.price ?? 0,
        }))
      : [{ productId: '', name: '', quantity: 1, price: 0 }],
  );

  const totals = computeTotals({ items, discount });

  const setItem = (index, patch) => {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  };

  const addRow = () => {
    setItems((prev) => [...prev, { productId: '', name: '', quantity: 1, price: 0 }]);
  };

  const removeRow = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const onChooseProduct = (index, productIdValue) => {
    const productId = productIdValue ? Number(productIdValue) : '';
    const product = products.find((p) => p.id === productId);
    if (!product) {
      setItem(index, { productId: '', name: '', price: 0 });
      return;
    }
    setItem(index, { productId: product.id, name: product.name, price: Number(product.price) || 0 });
  };

  const submit = (e) => {
    e.preventDefault();
    setFormError('');

    const normalizedItems = items
      .map((it) => ({
        productId: it.productId ? Number(it.productId) : null,
        name: String(it.name || '').trim(),
        quantity: Math.max(1, Number(it.quantity) || 1),
        price: Math.max(0, Number(it.price) || 0),
      }))
      .filter((it) => it.name.length > 0);

    for (const item of normalizedItems) {
      if (!item.productId) continue;
      const product = products.find((entry) => entry.id === item.productId);
      if (product && item.quantity > Number(product.stock || 0)) {
        setFormError(`Quantity for ${product.name} cannot exceed available stock (${product.stock}).`);
        return;
      }
    }

    onSubmit({
      customerName: String(customerName || '').trim(),
      phone: toPhoneLike(phone),
      items: normalizedItems,
      discount: Math.max(0, Number(discount) || 0),
    });
  };

  const canSubmit = customerName.trim().length > 0 && items.some((it) => String(it.name || '').trim().length > 0);

  return (
    <form onSubmit={submit} className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <FaUser className="text-sky-500" /> Customer Name
          </label>
          <input
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
            placeholder="e.g. Ali Khan"
            required
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <FaTag className="text-emerald-500" /> Phone Number
          </label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
            placeholder="e.g. 0300-1234567"
          />
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        {formError ? (
          <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {formError}
          </div>
        ) : null}
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <span className="grid h-8 w-8 place-items-center rounded bg-white text-[#00595A] shadow-sm">
              <FaShoppingCart className="h-4 w-4" />
            </span>
            Order Items
          </div>
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#00595A] px-3 py-2 text-sm font-semibold text-white transition hover:bg-[#004446]"
          >
            <FaPlus className="h-4 w-4" />
            Add item
          </button>
        </div>

        <div className="space-y-3">
          {items.map((it, idx) => (
            <div key={idx} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
                <div className="md:col-span-5">
                  <label className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Product</label>
                  <select
                    value={it.productId || ''}
                    onChange={(e) => onChooseProduct(idx, e.target.value)}
                    className="mt-1 block w-full rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
                  >
                    <option value="">Select product</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-3">
                  <label className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Qty</label>
                  <div className="mt-1 flex items-center rounded-lg border border-slate-200 bg-white shadow-sm">
                    <button
                      type="button"
                      onClick={() => setItem(idx, { quantity: Math.max(1, (Number(it.quantity) || 1) - 1) })}
                      className="grid h-11 w-11 place-items-center text-slate-600 transition hover:bg-slate-50"
                      aria-label="Decrease quantity"
                    >
                      <FaMinus className="h-3 w-3" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={it.productId ? (products.find((p) => p.id === Number(it.productId))?.stock ?? undefined) : undefined}
                      value={it.quantity}
                      onChange={(e) => setItem(idx, { quantity: Number(e.target.value) })}
                      className="h-11 w-full border-x border-slate-200 px-3 text-sm focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setItem(idx, { quantity: (Number(it.quantity) || 1) + 1 })}
                      className="grid h-11 w-11 place-items-center text-slate-600 transition hover:bg-slate-50"
                      aria-label="Increase quantity"
                    >
                      <FaPlus className="h-3 w-3" />
                    </button>
                  </div>
                </div>

                <div className="md:col-span-3">
                  <label className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Price</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={it.price}
                    onChange={(e) => setItem(idx, { price: Number(e.target.value) })}
                    className="mt-1 block w-full rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
                  />
                </div>

                <div className="md:col-span-1 flex items-end justify-end">
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-rose-100 text-rose-700 transition hover:bg-rose-200 disabled:opacity-50"
                    disabled={items.length === 1}
                    aria-label="Remove item"
                    title="Remove item"
                  >
                    <FaTimes className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm">
                <span className="text-slate-600">Line total</span>
                <span className="font-semibold text-slate-900">
                  ${(Math.max(1, Number(it.quantity) || 1) * (Number(it.price) || 0)).toFixed(2)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2 md:p-6">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <FaTag className="text-amber-500" /> Discount
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={discount}
            onChange={(e) => setDiscount(Number(e.target.value))}
            className="mt-1 block w-full rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
          />
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm md:p-6">
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Subtotal</span>
              <span className="font-semibold text-slate-900">${totals.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Discount</span>
              <span className="font-semibold text-slate-900">${totals.discount.toFixed(2)}</span>
            </div>
            <div className="h-px bg-slate-200" />
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Total</span>
              <span className="text-lg font-semibold text-slate-900">${totals.total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-[#00595A] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004446] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FaSave className="h-4 w-4" /> Save order
        </button>
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-100 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
          >
            <FaArrowLeft className="h-4 w-4" /> Back
          </button>
        ) : null}
      </div>
    </form>
  );
};

export default OrderForm;
